import { NgModule } from '@angular/core';
import { ConfigService } from './config.service';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import {
    MsalModule, MsalService, MsalGuard, MsalInterceptor,
    MsalBroadcastService, MsalInterceptorConfiguration, MsalGuardConfiguration,
    MSAL_INSTANCE, MSAL_GUARD_CONFIG, MSAL_INTERCEPTOR_CONFIG
} from '@azure/msal-angular';
import { BrowserCacheLocation, InteractionType, IPublicClientApplication, PublicClientApplication } from '@azure/msal-browser';

export function msalInstanceFactory(config: ConfigService): IPublicClientApplication {
    return new PublicClientApplication({
        auth: {
            clientId: config?.msalClientId,
            authority: config?.msalAuthority,
            redirectUri: window.location.origin + '/blank',
            postLogoutRedirectUri: window.location.origin + '/logout',
        },
        cache: {
            cacheLocation: BrowserCacheLocation.LocalStorage,
        },
        system: {
            allowRedirectInIframe: false // Prevent redirects in iframe
        }
    });
}

export function msalInterceptorConfigFactory(config: ConfigService): MsalInterceptorConfiguration {
    const protectedResourceMap = new Map<string, Array<string>>();
    protectedResourceMap.set(`${config.taApiBaseUrl}/`, [config.taApiScope]);
    protectedResourceMap.set(`${config.cmApiBaseUrl}/`, [config.cmApiScope]);
    protectedResourceMap.set(`${config.mapApiBaseUrl}/`, [config.mapApiScope]);
    protectedResourceMap.set(`${config.signalRHub}/negotiate`, [config.signalRScope]);
    protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['user.read']);

    // Detect if running in iframe - use popup instead of redirect to avoid errors
    const isIframe = window !== window.parent && !window.opener;
    const interactionType = isIframe ? InteractionType.Popup : InteractionType.Redirect;

    return {
        interactionType,
        protectedResourceMap,
        strictMatching: false // in v5 default is true, but it breaks the api calls. 
    };
}

export function msalGuardConfigFactory(): MsalGuardConfiguration {
    // Detect if running in iframe - use popup instead of redirect to avoid errors
    const isIframe = window !== window.parent && !window.opener;
    const interactionType = isIframe ? InteractionType.Popup : InteractionType.Redirect;

    return {
        interactionType,
        authRequest: {
            scopes: ['user.read']
        },
        loginFailedRoute: '/login'
    };
}

@NgModule({
    imports: [MsalModule]
})
export class MsalApplicationModule {
    static forRoot() {
        return {
            ngModule: MsalApplicationModule,
            providers: [
                ConfigService,
                {
                    provide: MSAL_INSTANCE,
                    useFactory: msalInstanceFactory,
                    deps: [ConfigService]
                },
                {
                    provide: MSAL_GUARD_CONFIG,
                    useFactory: msalGuardConfigFactory,
                    deps: [ConfigService]
                },
                {
                    provide: MSAL_INTERCEPTOR_CONFIG,
                    useFactory: msalInterceptorConfigFactory,
                    deps: [ConfigService]
                },
                MsalService,
                {
                    provide: HTTP_INTERCEPTORS,
                    useClass: MsalInterceptor,
                    multi: true
                },
                MsalGuard,
                MsalBroadcastService
            ]
        };
    }
}
